import axios, {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosRequestHeaders,
  AxiosResponse,
} from 'axios'
import { NextPageContext } from 'next/types'
import { toast } from 'react-toastify'
import { DefaultRootState } from 'react-redux'
import { getOSName } from '../helpers/props'
import RequestProtector from './service-helpers/RequestProtector'
import ToastController from './service-helpers/ToastController'
import { logout } from '@/redux/store/actions/auth'
import getPlatform from '@/helpers/getPlatform'
import { getClientBaseUrl } from '@/helpers/getClientBaseUrl'

interface ApiOpts {
  baseURL?: string
  getState?: () => DefaultRootState
  dispatch?: any
  headers?: AxiosRequestHeaders
  configs?: AxiosRequestConfig
  ctx?: NextPageContext
}
const DEFAULT_API_BASE = 'https://api.shab.ir/api/fa/sandbox/v_1_4'
const toastController = new ToastController()
class Api {
  private xhr: AxiosInstance
  private isClient: boolean
  ctx: NextPageContext | undefined
  opts: any

  constructor(opts: ApiOpts = {}) {
    this.isClient = typeof window !== 'undefined'
    this.opts = opts

    const headers = {
      'Content-Type': 'application/json; charset=UTF8',
      Platform: getPlatform(),
      ...opts.headers,
    }

    if (this.isClient) {
      const currentUrl = new URL(window.location.href)
      const queryParams = new URLSearchParams(currentUrl.search)
      const gpsAdid =
        queryParams.get('gps_adid') || sessionStorage.getItem('gps_adid')
      if (gpsAdid) {
        headers['gps-adid'] = gpsAdid
      }

      headers['Shab-App'] = getOSName()
    } else {
      if (!(opts?.headers?.cookie as string)?.match(/deviceId=([^;]+)/)) {
        headers['cookie'] =
          (headers['cookie'] ? headers['cookie'] + '; ' : '') +
          'deviceId=' +
          headers['x-deviceId']
      }
      if (opts.ctx) {
        if (opts.ctx.query?.gps_adid) {
          headers['gps-adid'] = opts.ctx.query?.gps_adid
        }
      }
    }

    this.xhr = axios.create({
      withCredentials: true,
      headers,
      ...(opts.configs ? opts.configs : {}),
    })

    this.xhr.interceptors.request.use(async config => {
      let baseURL: string | undefined
      if (config.baseURL) {
        return config
      }
      // Client Reqs
      if (typeof window !== 'undefined') {
        baseURL = getClientBaseUrl()
      }
      // Server Reqs
      else {
        baseURL = process.env.API_BASE_URL || DEFAULT_API_BASE
      }
      config.baseURL = this.opts.baseURL || baseURL
      return config
    })
  }

  private handleErr = (err: any) => {
    // Client ERR
    // if (this.isClient) {
    //   SentryClient.captureException(
    //     'client api error: ' + err?.response ? err.response : err?.toString(),
    //   )
    // }

    // HandleErr
    if (err && err?.response) {
      if (err.response.status >= 500) {
        if (this.isClient) {
          toastController.handleSetToast(() =>
            toast.error('خطای سرور، لطفا مجددا تلاش کنید', {
              position: 'top-right',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
            }),
          )
        }
      }
      if (err.response.status === 401) {
        if (this?.opts?.dispatch) {
          this?.opts.dispatch(logout())
        }
      }
    } else {
      if (this.isClient) {
        toastController.handleSetToast(() =>
          toast.info(
            'خطا در برقراری ارتباط با سرور. لطفا از اتصال خود به اینترنت مطمئن شوید..',
            {
              position: 'top-center',
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
              draggable: true,
              progress: undefined,
              bodyStyle: { textAlign: 'center' },
            },
          ),
        )
      }
    }
  }

  // private handleRes = (res: AxiosResponse) => {
  //   // HandleRes
  //   if (this.isClient) {
  //   }
  // }

  get(url: string, params?: any): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      const requestProtector = new RequestProtector({ url, params }, reject)

      requestProtector.handleRequest(setLoading => {
        if (setLoading) setLoading(true)
        this.xhr
          .request({
            method: 'GET',
            url,
            params,
          })
          .then(res => {
            if (setLoading) setLoading(false)
            resolve(res)

            // this.handleRes(res)
          })
          .catch(err => {
            if (setLoading) setLoading(false)
            reject(err)

            this.handleErr(err)
          })
          .finally(() => {
            if (setLoading) setLoading(false)
          })
      })
    })
  }

  post(
    url: string,
    params?: any,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      this.xhr
        .post(url, params, config)
        .then(res => {
          resolve(res)
          // this.handleRes(res)
        })
        .catch(err => {
          reject(err)
          this.handleErr(err)
        })
    })
  }

  put(url: string, params?: any): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      this.xhr
        .put(url, params)
        .then(res => {
          resolve(res)
          // this.handleRes(res)
        })
        .catch(err => {
          reject(err)
          this.handleErr(err)
        })
    })
  }

  delete(url: string, params?: any): Promise<AxiosResponse> {
    return new Promise((resolve, reject) => {
      this.xhr
        .delete(url, { data: params })
        .then(res => {
          resolve(res)
          // this.handleRes(res)
        })
        .catch(err => {
          reject(err)
          this.handleErr(err)
        })
    })
  }

  upload(url: string, data, uploadCallback) {
    return new Promise((resolve, reject) => {
      this.xhr
        .post(url, data, {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'multipart/form-data;',
          },
          onUploadProgress:
            uploadCallback &&
            (e => {
              const progress = Math.floor((e.loaded * 100) / e.total)
              uploadCallback(progress)
            }),
        })
        .then(res => {
          // this.handleRes(res)
          resolve(res)
        })
        .catch(err => {
          this.handleErr(err)
          reject(err)
        })
    })
  }
}

export default Api
